import type { Article } from './types'

/**
 * Help for the supervisor's phone screens (Crew Time, /field/*).
 *
 * Every label quoted in bold is copied from the screen it describes. When a
 * screen's wording changes, change the article in the same commit.
 */
export const FIELD_ARTICLES: Article[] = [
  // ---------------------------------------------------------------- /field
  {
    slug: 'field-today',
    title: 'Today: your starting screen',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Start a crew day, pick up an unfinished one, or record per diem for a day nobody worked.',
    routes: ['/field'],
    body: `**Today** is where you land after signing in. It does two things: start a crew day, or record per diem for a day nobody worked.

## What you can do here

- **Start crew day** opens [New crew day](/help/crew-day). Use it for any day the crew worked.
- **Continue crew day** shows instead when you have a crew day you have not sent yet. A yellow box above it says which job, which date and how many crew. Tap it to carry on where you left off.
- **Per diem only** is for days the crew is away but not working, like a weekend at the job. See [Per diem only](/help/per-diem-only).

After you send a crew day or per diem, you come back here and a blue box tells you what was saved, for example **Sent — 12 lines for 6 crew.**

## The buttons along the top

| Button | What it is for |
| --- | --- |
| **Today** | This screen |
| **New crew day** | Record hours for your crew |
| **Per diem** | Per diem with no hours |
| **My time** | Crew days you have recorded, a job and week at a time |
| **My crew** | Your crew's whole week, with corrections and per diem |

## Tips

- This screen loads nothing from the office, so it opens fast even on one bar of signal.
- Only one unfinished crew day is kept on your phone at a time. Finish and send it before starting the next.
- Working with no signal? Read [Working without signal](/help/working-offline).`,
  },

  // ------------------------------------------------------------ /field/new
  {
    slug: 'crew-day',
    title: 'Record a crew day',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Pick the job, date and crew, enter hours, tick per diem, and submit.',
    routes: ['/field/new'],
    body: `**New crew day** is how you record one day's work for your crew on one job. It has two steps: who and where, then hours.

## Step 1: job, date and crew

1. Under **Job**, type in **Search** (job number or name) and tap the job. A **Certified** badge shows on certified jobs. Tap **Change job** if you picked the wrong one.
2. Under **Date**, check the date. It starts on today.
3. Under **On site**, tap **I'm on site** while you are at the job. See [Location check-in](/help/location-check-in).
4. Under **Your crew**, tap your crew to tick everyone on it. Untick anyone who did not work.
5. If you sent a crew day for this job before on this phone, **Same crew as last time** ticks the same people in one tap. It shows only while nobody is ticked.
6. Need someone who is not on your crew? Tap **+ Add someone not on your crew**. See [People not on your crew](/help/floaters-and-new-hires).
7. Tap **Next — enter hours**.

## Step 2: hours

1. Use **Apply to the whole crew**: pick the **Type of work**, enter **Hours each**, and tap **Apply to all**. Everyone gets the same hours.
2. Fix anyone who differs on their own card. Type over their hours, or tap **✕** to drop a line.
3. **+ Add travel or other** adds a second line for one person. Travel has its own rules; see [Travel and drivers](/help/travel-and-drivers).
4. Tick **Per diem for the whole crew**, or tick **Per diem** on each person's card.
5. **Shift and work performed (optional)**: the crew's **Start**, **End** and **Work performed**. If one person's day was different, tap **Different shift or task** on their card. **Same as crew** undoes it.
6. Someone did not show up after all? Tap **Remove from day** on their card.
7. Add a **Note (optional)** for the office if needed.
8. Check the totals, then tap **Submit crew day**.

Submitting records the day and approves it. Nobody else signs off, so check the hours before you send.

## Warnings you may see

- **Already recorded elsewhere on this date:** someone on your list already has hours on another job that day. That is fine if they split the day between jobs. If not, untick them.
- **Certifications for this job:** someone is missing, or about to lose, a certification the job requires. You can still record them, and the office sees the same warning.

Neither warning stops you. They are there so you can catch a mistake.

## Why can't I...

- **...pick someone from another supervisor's crew?** You record your own crew and yourself. Ask the office to record anyone else. This stops two supervisors paying the same person twice.
- **...type a per diem amount?** Per diem is a tick, not a figure. The amount comes from the office's rate table, based on where the job is and each person's tier. If an amount looks wrong, tell the office.
- **...see a per diem tick for someone?** Nobody in the rate table has a rate for them on this job. If the whole card says **No per diem rate is set up for this job.**, the office needs to add one.
- **...send the day without a location?** You can. If you did not tap **I'm on site**, fill in **No location recorded — why? (required)** above the Submit button. Any reason works, like "no signal in the tank".
- **...enter 25 hours?** Each line must be between 0 and 24 hours.

## Tips

- Everything you type is saved on the phone as you go. A phone call or a locked screen will not lose it.
- Blank lines are not sent. If every line is blank you get **Nothing to submit — enter hours or an amount for at least one person.**
- **Back to crew** returns to step 1 without losing the hours you typed.
- Made a mistake after sending? Fix it from [My crew](/help/my-crew) or the [crew day screen](/help/crew-day-detail).`,
  },

  // ------------------------------------------------------- /field/per-diem
  {
    slug: 'per-diem-only',
    title: 'Per diem for a day nobody worked',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Record per diem with no hours, such as a weekend the crew stays at the job.',
    routes: ['/field/per-diem'],
    body: `The **Per diem only** screen records per diem for days the crew is away but not working, like a weekend in camp. No hours are recorded. If the crew worked that day, record a [crew day](/help/crew-day) instead and tick per diem there.

## How to record it

1. Under **Job**, search and tap the job the per diem belongs to. The rates for that job's location show under it.
2. Under **Days**, tap each day to tick it. You can pick days from **Last week** or **This week**.
3. If **Your crew** shows, tap your crew. Then tick people under **Crew**. **Everyone** ticks all who can draw per diem. **Clear all** unticks them.
4. Check the line above the button, for example **2 calendar days × 6 crew members = 12 per diems**.
5. Tap the **Record ... per diems** button. It shows the count and the total dollars.

## Rules, and why

- **You must pick the job every time.** Per diem is charged to a job, so the screen never guesses it. A wrong guess would put money on the wrong job without anyone noticing.
- **Nobody starts out ticked.** This is money for days nobody saw worked, so every name is a choice you make.
- **Rates come from the office.** Each person's amount comes from the office's rate table, based on the job's location and the person's tier. You cannot change it.
- **Only last week and this week.** A pay period ends on Saturday, so on a Sunday yesterday is already last week. Anything older is a correction for the office.
- **Future days are dimmed and marked not yet.** You can still tick them, for a crew that is already travelling.

## Why can't I...

- **...tick a person?** They show **no per diem**: there is no rate for them on this job. Office staff draw no per diem. Anyone else needs a tier set up by the office.
- **...save the days?** If someone shows **already paid** with a day, they already have per diem on that day, on any job. Untick them or untick that day. One person gets one per diem per day.
- **...see the Days or Crew sections?** The job has no per diem rate. A yellow box says why. Ask the office.
- **...record per diem for a paid week?** Once Sage has paid the period, per diem for it has to be handled in Sage.

If you see **Already recorded** with a list of names and days, nothing was saved. Untick those and try again.

## Tips

- This screen needs signal. It does not save a draft on the phone.
- To add or remove per diem on a grid of the whole week, use [My crew](/help/my-crew).`,
  },

  // --------------------------------------------------------- /field/review
  {
    slug: 'my-time',
    title: 'My time: review your recorded weeks',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'See the crew days you recorded, a job and week at a time, as a grid.',
    routes: ['/field/review'],
    body: `**My time** lists the crew days you recorded, grouped by job and pay week (Sunday to Saturday, the same weeks as My crew and payroll). Use it to check a week's hours for one job before payroll.

## How to review a week

1. Find the job and week. Each card shows the dates, number of days, crew, lines and total hours.
2. Tap **Review the week**.
3. **Hours by person and day** shows everyone down the side and each day across the top, with totals on both edges. Dollar amounts, like per diem, show in small print under the hours.
4. Under **Days submitted**, tap **Open** on a day to see its lines and fix them. See [A crew day, up close](/help/crew-day-detail).
5. Tap **← All weeks** to go back to the list.

## What the status means

| Status | Meaning |
| --- | --- |
| **Approved** | Recorded. You approved it when you sent it. You can still correct it. |
| **PostedToSage** | In Sage. It is locked here, and any fix is made in Sage. |

## Badges

- **Prevailing wage** or **Certified** on a job card means special pay rules apply. Take extra care with work types on those jobs.

## Why can't I...

- **...see a day my crew worked?** This screen shows only the crew days *you* recorded. If someone else recorded it, ask them or the office. To see your crew's whole week across every job, use [My crew](/help/my-crew).
- **...edit in the grid?** The grid is for reading. Open the day to correct it.

## Tips

- The grid scrolls sideways on a phone. Turn the phone on its side for a wider view.`,
  },

  // ------------------------------------------------------ /field/crew-week
  {
    slug: 'my-crew',
    title: 'My crew: the week, corrections and per diem',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'See your crew’s whole week across every job, fix hours, and add or remove per diem.',
    routes: ['/field/crew-week'],
    body: `**My crew** shows your crew's whole pay week across every job, laid out like the paper sheet: people down the side, Sunday to Saturday across. Use it to fix a wrong number and to sort out per diem.

## Picking the week

Use **Week ending** to pick a week. Pay periods end on a Saturday. The screen opens on the latest week that has time in it. The bar under the menu shows the week and whether it is **Open**, **Closed to supervisors**, **Paid** (with the check date), or has **No time entered**.

If the office has turned on the supervisor cutoff, the line under the week picker says when the week closes to your changes, for example *Supervisor changes close Tue 10 Nov, 11:59 PM (Pacific)*. After that, ask the office to make a change. They can also reopen the week for you for a while.

## Correct someone's hours

1. Tap the hours in a person's day.
2. The lines behind that day open, often just one, sometimes regular plus travel.
3. Type the reason in **Why the change? (required — e.g. typo)**.
4. Type the right hours and tap **Save**.

Every change you make needs a note, even just "typo". Management will want to know why, and the note is kept with the change.

## Add or remove per diem

1. Tap **Per diem** beside the crew's name. A row of tick boxes appears under each person.
2. Tick an empty box to add per diem for that day. Days that already have per diem show ticked; untick one to take it off.
3. If you are adding days, pick the **Job for the days you are adding**. It is never filled in for you, because per diem is charged to a job.
4. If you are taking days off, fill in **Why is per diem coming off ... days?**
5. Tap **Save**. The button says what will happen, for example **Save: add 2 days, remove 1 day**.

Tap **Cancel** to leave without changes.

## Why can't I...

- **...change a per diem amount?** Amounts come from the office's rate table, based on the job's location and each person's tier. You can add or remove a day, but only the office can change what it pays.
- **...tap an empty day?** Only a day with hours has lines to correct. An empty day is a per diem question; use the **Per diem** button.
- **...save a line marked with a yellow badge?** That line is locked. It has already gone to Sage, Sage has paid that week, or the week has closed to supervisors. The badge says which. Ask the office.
- **...see the Per diem button?** Sage has already paid that week, or the week has closed to supervisors (the office sets when, under [Supervisor cutoff](/help/supervisor-cutoff)).
- **...remove per diem?** Removals all go through or none do. If any day you unticked is already in Sage or in a paid week, nothing changes and the message says why.

## Tips

- A name with **2 jobs** (or more) moved between jobs that week.
- To change a work type, remove a line, or move a whole day to another job or date, open the day from [My time](/help/my-time). See [A crew day, up close](/help/crew-day-detail).`,
  },

  // ---------------------------------------------- /field/submissions/:id
  // TODO(verify): the brief mentions "add lines" on this screen. The field
  // view of SubmissionDetailPage has no add-line control (the API supports it;
  // only the office labor register uses it), so the article leaves it out.
  {
    slug: 'crew-day-detail',
    title: 'A crew day, up close: correct or remove lines',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Open one crew day to fix a line, remove one, or move the day to another job or date.',
    routes: ['/field/submissions'],
    body: `This screen shows one crew day: who worked, each line, and every change made since. Open it from [My time](/help/my-time): **Review the week**, then **Open** on a day.

## What is on the screen

- The job, date, crew, lines and hours, and who entered the day.
- **Approved on entry** means the supervisor who recorded the day also approved it.
- If someone tapped **I'm on site**, a box shows when, how accurate the reading was, and how far it was from the job's pin. If nobody did, it shows the reason given.
- **Shift and work performed**, if any were recorded.
- **Lines**: each person's work type, hours or amount, and its Sage status. A line for travel charged to another job shows **↳ charged to** with that job.
- **Correction history**: every change, who made it, the person and **work type** it was on, and the reason.

## Correct a line

1. Type a reason in **Reason for corrections** first. It is required for every change, even just "typo".
2. Tap **Correct** on the line.
3. Change the work type or the hours, then tap **Save**. **Cancel** backs out.

The reason you typed is saved with every change you make while it is filled in.

## Remove a line

1. Fill in **Reason for corrections**.
2. Tap **Remove** on the line and confirm.

## Wrong job or date?

Use **Move this day** to move the whole day. See [Move a crew day](/help/move-a-crew-day).

## Why can't I...

- **...correct a per diem line?** Per diem amounts come from the office's rate table. A per diem line has only **Remove**. You also cannot change another line into per diem.
- **...change anything at all?** The day has posted to Sage. A yellow box says so. Corrections for posted time are made in Sage.
- **...change one line?** It shows **locked**: it has already gone to Sage.
- **...save a change at all, with a message about the week being closed?** The week has closed to supervisor changes. Ask the office; they can make the change or reopen the week.

## Tips

- To fix only the hours, [My crew](/help/my-crew) is quicker.
- Tap **← Back** to return to the week.`,
  },

  // -------------------------------------------------------- topic: move
  {
    slug: 'move-a-crew-day',
    title: 'Move a crew day to another job or date',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Recorded the day on the wrong job or date? Move the whole day in one go.',
    body: `If you recorded a crew day on the wrong job or the wrong date, move it. Do not delete the lines and enter them again. Moving keeps the history and takes everything with it.

## How to move a day

1. Open the day from [My time](/help/my-time): **Review the week**, then **Open**.
2. Type why in **Reason for corrections**, for example "picked the wrong job".
3. Under **Wrong job or date?**, tap **Move this day**.
4. Pick the right **Job**, the right **Date**, or both.
5. Tap **Check the move**. Nothing changes yet. The screen shows anything worth knowing, or **Nothing to flag. Ready to move.**
6. Read any warnings, then tap **Move to ... on ...** to do it. **Cancel** backs out.

## What moves with it

Every line, the per diem and the location reading move together. The day is never left half in each place.

- **Per diem is re-priced** for the new job's location, from the office's rate table.
- **The location reading moves too.** You really were where you tapped; only the job you picked was wrong.
- **Work types are not changed.** If the new job has different pay rules, for example it is prevailing wage, the day is flagged for the office to check the work types. The message says **Flagged for the office to check the work types.**

## Why can't I...

- **...move a day someone else recorded?** Only the supervisor who recorded the day, or the office, can move it.
- **...move it at all?** It is refused when:
  - any line has already gone to Sage,
  - either the old or the new date is in a week Sage has paid,
  - someone worked out of trade and the new job has no rate for that trade,
  - someone would get two per diems on the new date,
  - the new job cannot price someone's per diem.
- **...tap Check the move?** Pick a different job or date first.

When the move is refused, nothing changes. The message says why. Ask the office if you need it moved anyway.`,
  },

  // ------------------------------------------------------ topic: offline
  {
    slug: 'working-offline',
    title: 'Working without signal',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'How a crew day is kept safe on your phone, and what to do when you get signal back.',
    body: `Job sites often have no signal. You can still fill in a crew day. Sending it needs signal.

## What is saved on your phone

- **Your crew day, as you type.** Every tap and every number is saved on the phone. A phone call, a locked screen or a refresh will not lose it. You come back to the same step.
- **The job and crew lists.** If the phone cannot reach the office, a yellow box says **Showing saved job and crew lists — could not reach the server.** You can keep entering.
- **Your location tap.** **I'm on site** works without signal. The reading is kept with the crew day and sent with it.
- **Your last crew for each job**, for **Same crew as last time**.

## Sending it

1. Fill in the crew day as usual and tap **Submit crew day**.
2. If there is no signal, you see an error and the day stays on your phone.
3. When you have signal again, open **Today** and tap **Continue crew day**, or open **New crew day**.
4. Tap **Submit crew day** again.

It does not send by itself. Check that you see **Sent — ...** on **Today**.

## Can it be sent twice?

No. Each crew day carries its own tag from the moment you start it. If the first send got through but your phone did not hear back, sending again just says **That crew day was already recorded.** Nothing is doubled.

## Things to know

- Only **one** unfinished crew day is kept on a phone. Send it before starting the next one.
- The draft lives on that phone only. You cannot finish it on another phone or computer.
- **Per diem only**, **My time** and **My crew** need signal. They do not save drafts.
- If the location reading fails to send after the day goes through, the message says **Location NOT saved**. Your hours are still safe.`,
  },

  // ----------------------------------------------------- topic: location
  {
    slug: 'location-check-in',
    title: 'Location check-in: "I’m on site"',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Record that you were at the job, and what to do when you can’t.',
    body: `The **I'm on site** button records that *you* were at the job, with the time you tapped. It is one tap a day, on the crew day screen.

## How to check in

1. Start a [crew day](/help/crew-day) and pick the job.
2. Under **On site**, tap **I'm on site** while you are at the job.
3. Allow location if the phone asks.
4. A green box shows **Location recorded at** the time, and how accurate it is, for example **accurate to about 12 m**.
5. Carry on with your crew. The reading is sent with the crew day.

Tap **Take it again** if you want a better reading.

## Do it in the morning, on site

Tap when you arrive, not when you fill in hours at the hotel that night. The reading records where you were when you tapped, and the tap time is always shown. A tap at 9:30 pm from the hotel shows exactly that.

## If you can't get a reading

The day records either way. Inside a steel tank, or with location turned off, you may get **Could not get a location just now.** or **Location is turned off for this site.**

When you submit with no reading, fill in **No location recorded — why? (required)**. Any reason works, such as "at the hotel" or "no signal on site".

## Rules, and why

- **It records only you.** Your phone proves you were there, not the whole crew.
- **It belongs to one job.** If you change the job after tapping, the reading is dropped. Otherwise one job's location would land on another. Tap again on the right job.
- **It is never silent.** The portal only reads your location when you tap.
- **Pick a job first.** Without one, the tap says **Pick a job first.**

## Where it shows

The office sees each reading on the crew day, with how far it was from the job's pin. If the day was moved to the right job later, the reading moves with it.`,
  },

  // ----------------------------------------------------- topic: floaters
  {
    slug: 'floaters-and-new-hires',
    title: 'People not on your crew, and new hires',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Pick up someone who is on no crew for the day, or add a new hire the office has not entered yet.',
    body: `Some field staff are not on any crew and move between crews. You can pick them up for a day. You can also add a brand-new hire so their hours are not lost.

## Add someone who is on no crew

1. On step 1 of a [crew day](/help/crew-day), tap **+ Add someone not on your crew**. The number shows how many people are available.
2. Use **Search by name or number** to find them, and tap to tick.
3. Tap **Done**.

They now show in your **Crew** list under **Added for today**, with a **not your crew** badge. This is for today only. It does not move them onto your crew.

## Add a new hire

For someone who started today and is not in the system yet.

1. Open **+ Add someone not on your crew**.
2. Search first. They may already be listed, and finding them beats making a duplicate.
3. Tap **Not on this list? Add someone new to the company**.
4. Enter **First name** and **Last name**.
5. In **Anything that will help the office identify them**, add something useful, like the start date and job.
6. Tap **Add to today**.

They are ticked onto the day with a **new hire** badge. Enter their hours like anyone else.

## Rules, and why

- **Only people on no crew are listed.** Another supervisor's crew is theirs to record. This keeps two supervisors from paying the same person twice.
- **New-hire hours are held.** They are not sent to payroll until the office matches the person to their real employee record. Your note helps the office do that.
- **Watch for "Already recorded elsewhere on this date".** Because floaters can work for anyone, check this warning. A split day between two jobs is fine. A day recorded twice is not.

## Tips

- If you need someone who is on another supervisor's crew, ask the office to record them.`,
  },

  // ------------------------------------------------------- topic: travel
  {
    slug: 'travel-and-drivers',
    title: 'Travel, drivers, and travel to another job',
    section: 'Crew time (field)',
    audience: 'field',
    summary: 'Record travel hours, mark who drove, and charge travel to the job the crew drove to.',
    body: `Travel is recorded as its own line, on top of regular hours. Who drove changes the rate, and travel to a new job can be charged to that job.

## Add travel hours

For the whole crew:

1. On step 2 of a [crew day](/help/crew-day), in **Apply to the whole crew**, pick travel in **Type of work**.
2. Enter **Hours each** and tap **Apply to all**.

For one person, tap **+ Add travel or other** on their card and pick travel in the new line.

To undo travel for everyone, keep travel picked in **Type of work** and tap the **Remove ... from everyone** button.

## Mark who drove

Once someone has travel hours, their card shows **Drove on this job today**.

- Ticked: their travel pays the driver rate.
- Not ticked: their travel pays the rider rate.

This starts clear every day, because the same person may drive one day and ride the next. Tick it each time.

## Travel to a different job

A crew often works one job, then drives to the next one. The office charges that drive to the job they drove *to*.

1. Once there are travel hours, a **Travel** card appears.
2. Tick **Travelled to a different project**.
3. Pick the job in **Travelled to**. **Your jobs** are listed first, then **All open jobs**.
4. It applies to everyone's travel. If one person went somewhere else, change their travel line's job, or pick **Stayed on this job**.

## Rules, and why

- **Only travel can be charged to another job.** Regular hours stay on the crew day's job, because per diem and prevailing wage rates come from that job.
- **The destination must be an open job.**
- **Unticking Travelled to a different project** puts all travel back on the crew day's job.

## Tips

- Someone who only rode out and did no regular work: remove their regular line with **✕** and keep the travel line.`,
  },
]
